// src/pages/CadastroUsuario.js - VERSÃO FINAL IMPLEMENTADA
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, Chip, Alert,
  CircularProgress, Paper, Divider, Tooltip, MenuItem, FormControl,
  InputLabel, Select, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon,
  AdminPanelSettings as AdminIcon, Security as SecurityIcon, Visibility,
  VisibilityOff, Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const CadastroUsuario = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [conectividade, setConectividade] = useState('checking'); // 'checking', 'online', 'offline'
  const [novoUsuario, setNovoUsuario] = useState({
    usuario: '', nome_completo: '', senha: '', confirmarSenha: '', nivel: 1
  });

  const { api, NIVEIS, getNivelNome } = useAuth();

  // Verificar conectividade com o backend
  const verificarConectividade = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3003/api'}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000) // Timeout de 5 segundos
      });
      
      if (response.ok) {
        setConectividade('online');
        return true;
      } else {
        setConectividade('offline');
        return false;
      }
    } catch (error) {
      console.error('❌ Erro de conectividade:', error);
      setConectividade('offline');
      return false;
    }
  };

  const niveis = [
    { value: NIVEIS.EXPEDICAO, label: 'Expedição' },
    { value: NIVEIS.LABORATORIO, label: 'Laboratório' },
    { value: NIVEIS.ADMINISTRADOR, label: 'Administrador' }
  ];

  const carregarUsuarios = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔄 Iniciando carregamento de usuários...');
      console.log('🌐 API Base URL atual:', process.env.REACT_APP_API_URL || 'URL não configurada');
      
      const response = await api.listarUsuarios();
      console.log('📥 Resposta da API:', response);
      
      if (response && response.success) {
        console.log('✅ Usuários carregados com sucesso:', response.dados?.length || 0);
        setUsuarios(response.dados || []);
      } else {
        console.error('❌ Resposta de erro da API:', response);
        throw new Error(response?.message || 'Resposta inválida da API');
      }
    } catch (err) {
      console.error('❌ Erro detalhado ao carregar usuários:', err);
      console.error('📍 Stack trace:', err.stack);
      
      // Mensagens de erro mais específicas
      let mensagemErro = 'Erro ao carregar lista de usuários';
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        mensagemErro = 'Erro de conexão: Verifique se o backend está rodando';
      } else if (err.message.includes('CORS')) {
        mensagemErro = 'Erro de CORS: Problema de configuração do servidor';
      } else if (err.message.includes('401') || err.message.includes('unauthorized')) {
        mensagemErro = 'Erro de autenticação: Faça login novamente';
      } else if (err.message.includes('404')) {
        mensagemErro = 'Erro 404: Endpoint não encontrado';
      } else if (err.message.includes('500')) {
        mensagemErro = 'Erro interno do servidor';
      }
      
      setError(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    const inicializar = async () => {
      console.log('🚀 Inicializando página de usuários...');
      const conectado = await verificarConectividade();
      
      if (conectado) {
        await carregarUsuarios();
      } else {
        setError('Não foi possível conectar ao servidor. Verifique se o backend está rodando.');
      }
    };
    
    inicializar();
  }, []);

  const handleAdicionarUsuario = () => {
    setEditandoUsuario(null);
    setNovoUsuario({ usuario: '', nome_completo: '', senha: '', confirmarSenha: '', nivel: 1 });
    setDialogAberto(true);
  };

  const handleEditarUsuario = (usuario) => {
    setEditandoUsuario(usuario);
    setNovoUsuario({
      usuario: usuario.usuario,
      nome_completo: usuario.nome_completo || '',
      senha: '',
      confirmarSenha: '',
      nivel: usuario.nivel
    });
    setDialogAberto(true);
  };

  const handleSalvarUsuario = async () => {
    if (!novoUsuario.usuario.trim()) return setError('Nome de usuário é obrigatório');
    if (!editandoUsuario && !novoUsuario.senha) return setError('Senha é obrigatória para novos usuários');
    if (novoUsuario.senha && novoUsuario.senha !== novoUsuario.confirmarSenha) return setError('As senhas não coincidem');
    
    setError('');
    setSuccess('');
    
    try {
      const dadosParaEnviar = {
        nome_completo: novoUsuario.nome_completo,
        nivel: novoUsuario.nivel,
      };
      
      if (!editandoUsuario) {
        dadosParaEnviar.usuario = novoUsuario.usuario;
      }
      if (novoUsuario.senha) {
        dadosParaEnviar.senha = novoUsuario.senha;
      }

      console.log('💾 Salvando usuário:', { 
        acao: editandoUsuario ? 'editar' : 'criar',
        dados: { ...dadosParaEnviar, senha: dadosParaEnviar.senha ? '[OCULTA]' : 'N/A' }
      });

      let response;
      if (editandoUsuario) {
        response = await api.atualizarUsuario(editandoUsuario.id, dadosParaEnviar);
        console.log('✅ Usuário atualizado:', response);
        setSuccess('Usuário atualizado com sucesso!');
      } else {
        response = await api.criarUsuario(dadosParaEnviar);
        console.log('✅ Usuário criado:', response);
        setSuccess('Usuário criado com sucesso!');
      }
      
      setDialogAberto(false);
      await carregarUsuarios();
      
    } catch (err) {
      console.error('❌ Erro ao salvar usuário:', err);
      
      let mensagemErro = 'Erro ao salvar usuário';
      
      if (err.message.includes('409') || err.message.includes('já existe')) {
        mensagemErro = 'Nome de usuário já existe';
      } else if (err.message.includes('400')) {
        mensagemErro = 'Dados inválidos: Verifique os campos preenchidos';
      } else if (err.message.includes('401')) {
        mensagemErro = 'Sem permissão para criar usuários';
      } else if (err.message.includes('403')) {
        mensagemErro = 'Acesso negado: Apenas administradores podem criar usuários';
      }
      
      setError(mensagemErro);
    }
  };

  const handleExcluirUsuario = async (usuario) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário "${usuario.usuario}"?`)) {
      setError('');
      setSuccess('');
      try {
        await api.excluirUsuario(usuario.id);
        setSuccess('Usuário excluído com sucesso!');
        await carregarUsuarios();
      } catch (err) {
        setError(err.message || 'Erro ao excluir usuário');
      }
    }
  };

  const formatarData = (dataString) => {
    if (!dataString || dataString === 'N/A') return 'N/A';
    return new Date(dataString).toLocaleDateString('pt-BR');
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)', color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">Cadastro de Usuários</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Gerencie usuários e níveis de acesso
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            onClick={handleAdicionarUsuario} 
            startIcon={<AddIcon />} 
            sx={{ bgcolor: '#FF9800', '&:hover': { bgcolor: '#f57c00' } }}
            disabled={conectividade !== 'online'}
          >
            Novo Usuário
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      
      {/* Indicador de Conectividade */}
      {conectividade === 'offline' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ Sem conexão com o servidor. Verifique se o backend está rodando em localhost:3003
        </Alert>
      )}
      
      {conectividade === 'checking' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          🔍 Verificando conectividade com o servidor...
        </Alert>
      )}

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Lista de Usuários</Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={carregarUsuarios}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              Recarregar
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : usuarios.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="text.secondary">
                Nenhum usuário encontrado
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow><TableCell>Usuário</TableCell><TableCell>Nome Completo</TableCell><TableCell align="center">Nível</TableCell><TableCell align="center">Criado em</TableCell><TableCell align="center">Ações</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.map((usuario) => (
                    <TableRow key={usuario.id} hover>
                      <TableCell>{usuario.usuario}</TableCell>
                      <TableCell>{usuario.nome_completo || 'N/A'}</TableCell>
                      <TableCell align="center"><Chip label={getNivelNome(usuario.nivel)} color={usuario.nivel === 3 ? 'error' : usuario.nivel === 2 ? 'warning' : 'info'} size="small" /></TableCell>
                      <TableCell align="center">{formatarData(usuario.created_at)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditarUsuario(usuario)} color="primary"><EditIcon /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleExcluirUsuario(usuario)} color="error" disabled={usuario.id === 1}><DeleteIcon /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      
      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editandoUsuario ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}><TextField fullWidth label="Nome de Usuário" value={novoUsuario.usuario} onChange={(e) => setNovoUsuario({ ...novoUsuario, usuario: e.target.value })} disabled={!!editandoUsuario} /></Grid>
              <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Nível de Acesso</InputLabel><Select value={novoUsuario.nivel} label="Nível de Acesso" onChange={(e) => setNovoUsuario({ ...novoUsuario, nivel: e.target.value })}>{niveis.map((n) => (<MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>))}</Select></FormControl></Grid>
              <Grid item xs={12}><TextField fullWidth label="Nome Completo" value={novoUsuario.nome_completo} onChange={(e) => setNovoUsuario({ ...novoUsuario, nome_completo: e.target.value })} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label={editandoUsuario ? "Nova Senha (opcional)" : "Senha"} type={mostrarSenha ? 'text' : 'password'} value={novoUsuario.senha} onChange={(e) => setNovoUsuario({ ...novoUsuario, senha: e.target.value })} InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setMostrarSenha(!mostrarSenha)}>{mostrarSenha ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>),}}/></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Confirmar Senha" type={mostrarSenha ? 'text' : 'password'} value={novoUsuario.confirmarSenha} onChange={(e) => setNovoUsuario({ ...novoUsuario, confirmarSenha: e.target.value })} error={novoUsuario.senha !== novoUsuario.confirmarSenha} helperText={novoUsuario.senha !== novoUsuario.confirmarSenha ? 'As senhas não coincidem' : ''} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
          <Button onClick={handleSalvarUsuario} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CadastroUsuario;